import { supabase } from '../supabase';

/** Personal Testing Station — the guardian-context counterpart to
 * teamTestingService.js's recordTeamTestAttempt(). Guardian-only
 * (independently re-verified server-side, 053, via the same
 * guardian_athletes relationship every other guardian-facing write in
 * this app already uses) — NO Team, NO team_id, NO team_athletes
 * membership of any kind, ever.
 *
 * sessionId behaves exactly like its Team counterpart: OPTIONAL,
 * transient client-side state only, never persisted or looked up by
 * athlete+date. Omit it for the first attempt of this station visit;
 * pass back the `session_id` this function just returned for a
 * SUBSEQUENT attempt (same test or a different one) during the SAME
 * uninterrupted visit, so it correctly accumulates into one testing
 * occurrence instead of starting a new one every time.
 *
 * captureMethod — 'manual' (Own Stopwatch / direct entry) or
 * 'app_stopwatch' (FFN Stopwatch, performance.now()-derived) — the same
 * narrow allow-list 049/052 already enforce for Team Testing, re-
 * validated independently server-side (053). Never affects
 * source_type ('guardian', fixed) or verification_status ('personal',
 * fixed) — and this RPC never sets team_id, so the resulting session
 * is never Team/Program leaderboard eligible.
 *
 * Testing Occurrence / Attempt Protocol (059) — the response now also
 * carries occurrence_id/occurrence_status/valid_attempt_count/
 * planned_max_attempts, so the Station learns "are we done for today"
 * from this same round trip. The server (059) is the ONLY authority on
 * the attempt cap (default 3, from test_types.recommended_attempts) —
 * Personal Testing gets NO Change Max or Reopen privilege in V1; once
 * an occurrence completes, only End Test Early (guardian-authorized)
 * ever touched it, and no further attempt is ever accepted through this
 * RPC for that occurrence.
 *
 * Returns { session_id, attempt_id, attempt_number, occurrence_id,
 * occurrence_status, valid_attempt_count, planned_max_attempts }. */
export async function recordPersonalTestAttempt({ athleteId, testType, resultValue, testDate, sessionId, captureMethod }) {
  const { data, error } = await supabase.rpc('record_personal_test_attempt', {
    p_athlete_id: athleteId,
    p_test_type: testType,
    p_result_value: resultValue,
    p_test_date: testDate,
    p_session_id: sessionId || null,
    p_capture_method: captureMethod || 'manual',
  });
  if (error) throw error;
  return data?.[0] || null;
}

/** Testing Occurrence / Attempt Protocol (059) — the guardian/athlete's
 * own "I'm done" action, ending an ACTIVE occurrence before its planned
 * maximum is reached. Never triggered automatically by a PR. Personal
 * Testing has no Change Max or Reopen privilege in V1 — this is the
 * ONLY occurrence-management action exposed here. Returns the updated
 * test_occurrences row. */
export async function endPersonalTestOccurrenceEarly({ occurrenceId, note }) {
  const { data, error } = await supabase.rpc('end_test_occurrence_early', {
    p_occurrence_id: occurrenceId,
    p_reason_code: null,
    p_note: note || null,
  });
  if (error) throw error;
  return data;
}

/** Test Result Correction/Invalidation (055) — the guardian-context
 * counterpart to invalidateTeamTestAttempt(). Authorization (an active
 * guardian_athletes relationship for the attempt's own session's
 * athlete, or platform admin) is derived server-side entirely from the
 * attempt's own session; no athlete_id is passed here. Returns the
 * updated test_attempts row. */
export async function invalidatePersonalTestAttempt({ attemptId, reason, note }) {
  const { data, error } = await supabase.rpc('invalidate_personal_test_attempt', {
    p_attempt_id: attemptId,
    p_reason: reason,
    p_note: note || null,
  });
  if (error) throw error;
  return data;
}

/** Test Result Correction/Invalidation (055) — the guardian-context
 * counterpart to correctTeamTestAttempt(). Same atomic invalidate +
 * replace semantics, Personal context. Returns
 * { session_id, attempt_id, attempt_number } for the NEW replacement
 * row. */
export async function correctPersonalTestAttempt({ attemptId, correctedResultValue, reason, note }) {
  const { data, error } = await supabase.rpc('correct_personal_test_attempt', {
    p_attempt_id: attemptId,
    p_corrected_result_value: correctedResultValue,
    p_reason: reason,
    p_note: note || null,
  });
  if (error) throw error;
  return data?.[0] || null;
}
